import { create } from 'zustand';
import { registerRevitModelCacheInvalidator } from '../../../shared/lib/revit/modelCacheInvalidation';
import {
  markMappingRepairDocumentClosing,
  markMappingRepairDocumentSaved,
} from '../lib/mappingProfileProjectTrust';
import { traceMappingFlow } from '../lib/mappingTrace';
import { useAuthStore } from '../../../shared/stores/useAuthStore';
import type {
  ProjectProfileManualSelection,
  ProjectSessionProfileConfirmationSource,
} from './projectProfileGateContracts';

interface MappingProjectSessionConfirmation {
  profileKey: string;
  source: ProjectSessionProfileConfirmationSource;
}

/**
 * One home for the per-document-generation session state of the mapping
 * workspace: gate confirmations, manual profile selection, repair consent,
 * and the applied-profile marker. These used to live in three separate hooks
 * (profile controller, project profile gate, workspace content model), each
 * with its own reset wiring — the reopen-unsaved bug came from them drifting.
 * Everything here resets whenever the active document generation changes (DocumentOpened,
 * DocumentChanged, DocumentClosed) because it asserts something about
 * project content that may no longer be true. DocumentChanged/DocumentClosed reach
 * this store only when the active document identity actually changed (same-document
 * events are filtered upstream), so they are switch signals, not model edits.
 * DocumentClosing is the earliest reliable signal that the current project is
 * going away; the events naming the surviving project may be deduped or ignored
 * once the follow-up identity probe has already adopted it.
 */
interface MappingProjectSessionState {
  confirmations: Record<string, MappingProjectSessionConfirmation>;
  manualSelection: ProjectProfileManualSelection | null;
  repairConsentProfileKey: string | null;
  repairConsentProjectTrustExemptionProfileKey: string | null;
  lastAppliedProfileKey: string;
  confirmProfileForProject: (
    projectKey: string,
    profileKey: string,
    source: ProjectSessionProfileConfirmationSource
  ) => void;
  setManualSelection: (
    selection: ProjectProfileManualSelection | null | ((current: ProjectProfileManualSelection | null) => ProjectProfileManualSelection | null)
  ) => void;
  setRepairConsentProfileKey: (
    profileKey: string | null,
    options?: { allowWithoutProjectTrust?: boolean }
  ) => void;
  setLastAppliedProfileKey: (profileKey: string | ((current: string) => string)) => void;
}

const initialSessionState = {
  confirmations: {},
  manualSelection: null,
  repairConsentProfileKey: null,
  repairConsentProjectTrustExemptionProfileKey: null,
  lastAppliedProfileKey: '',
};

export const useMappingProjectSessionStore = create<MappingProjectSessionState>()((set) => ({
  ...initialSessionState,
  confirmProfileForProject: (projectKey, profileKey, source) =>
    set((state) => ({
      confirmations: { ...state.confirmations, [projectKey]: { profileKey, source } },
    })),
  setManualSelection: (selection) =>
    set((state) => ({
      manualSelection: typeof selection === 'function' ? selection(state.manualSelection) : selection,
    })),
  setRepairConsentProfileKey: (profileKey, options) => set({
    repairConsentProfileKey: profileKey,
    repairConsentProjectTrustExemptionProfileKey:
      profileKey && options?.allowWithoutProjectTrust === true ? profileKey : null,
  }),
  setLastAppliedProfileKey: (profileKey) =>
    set((state) => ({
      lastAppliedProfileKey:
        typeof profileKey === 'function' ? profileKey(state.lastAppliedProfileKey) : profileKey,
    })),
}));

registerRevitModelCacheInvalidator((event) => {
  const activeUserId = useAuthStore.getState().user?.id ?? null;
  if (event.eventType === 'DocumentSaved' || event.eventType === 'DocumentSavedAs') {
    const record = markMappingRepairDocumentSaved(
      activeUserId,
      event.documentPath,
      event.previousDocumentPath
    );
    traceMappingFlow('mapping repair persistence document saved', {
      projectKey: event.documentPath,
      previousProjectKey: event.previousDocumentPath,
      repairPersistenceStatus: record?.repairPersistence?.status ?? null,
    });
  } else if (event.eventType === 'DocumentClosing') {
    const closingProjectKey = event.documentPath ??
      (event.documentName ? `name:${event.documentName}` : null);
    const record = markMappingRepairDocumentClosing(
      activeUserId,
      closingProjectKey,
      event.isModified
    );
    traceMappingFlow('mapping repair persistence document closing', {
      projectKey: closingProjectKey,
      isModified: event.isModified ?? null,
      repairPersistenceStatus: record?.repairPersistence?.status ?? null,
    });
  }

  if (
    event.eventType === 'DocumentOpened' ||
    event.eventType === 'DocumentChanged' ||
    event.eventType === 'DocumentClosing' ||
    event.eventType === 'DocumentClosed'
  ) {
    useMappingProjectSessionStore.setState(initialSessionState);
  }
});

export function __resetMappingProjectSessionForTests(): void {
  useMappingProjectSessionStore.setState(initialSessionState);
}
